import clsx from "clsx";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useMemo } from "react";
import styles from "./Calendar.module.scss";

export type TCalendarValue = [Dayjs | null, Dayjs | null] | (Dayjs | null);
export type CalendarProps = {
  value?: TCalendarValue;
  onDateClick?: (date: Dayjs) => void;
  monthIndex: number;
  year: number;
};

function isLeapYear(year) {
  return (
    (year - 474) % 128 !== 30 && (((year - 474) % 128) * 31 + 46) % 128 < 31
  );
}
const week = [
  "شنبه",
  "یک‌شنبه",
  "دوشنبه",
  "سه‌شنبه",
  "چهارشنبه",
  "پنج‌شنبه",
  "جمعه",
];
export const useCalendar = (options: CalendarProps) => {
  const { value, monthIndex, year } = options;

  const date = useMemo(
    () => dayjs(`${year}-${monthIndex + 1}-1`, { format: "YYYY-M-D" }),
    [value, year, monthIndex],
  );

  const leapYear = useMemo(() => isLeapYear(year), [year]);
  const months = useMemo(
    () => [
      { name: "فروردین", days: 31 },
      { name: "اردیبهشت", days: 31 },
      { name: "خرداد", days: 31 },
      { name: "تیر", days: 31 },
      { name: "مرداد", days: 31 },
      { name: "شهریور", days: 31 },
      { name: "مهر", days: 30 },
      { name: "آبان", days: 30 },
      { name: "آذر", days: 30 },
      { name: "دی", days: 30 },
      { name: "بهمن", days: 30 },
      { name: "اسفند", days: leapYear ? 30 : 29 },
    ],
    [leapYear],
  );
  return {
    months,
    month: months[monthIndex],
    week,
    leapYear,
    date,
  };
};

const Calendar = (props: CalendarProps) => {
  const { value, onDateClick } = props;
  const { month, week, date } = useCalendar(props);

  const offsetValue = useMemo(() => {
    return date.calendar("jalali").startOf("month").day() + 1;
  }, [date]);

  const dates = useMemo(
    () => [
      ...(offsetValue !== 7
        ? [...new Array(offsetValue).keys()].map((_i, i) => (
            <div
              key={i + "_offset"}
              className={clsx(styles["date"], styles["date--offset"])}
            />
          ))
        : []),
      ...[...new Array(month.days).keys()].map((i) => {
        const d = date
          .clone()
          .calendar("jalali")
          .set("date", i + 1);
        const formatedDate = d.standardFormat({ jalali: true });
        return (
          <div
            key={d + "_key"}
            data-date={formatedDate}
            className={clsx(
              styles["date"],
              Array.isArray(value)
                ? [
                    value[0]?.standardFormat({ jalali: true }) ==
                      formatedDate && [
                      styles["date--selected"],
                      styles["date--selected-start"],
                    ],
                    value[1]?.standardFormat({ jalali: true }) ==
                      formatedDate && [
                      styles["date--selected"],
                      styles["date--selected-end"],
                    ],
                    value[0] &&
                      value[1] &&
                      value[0] < d &&
                      value[1] > d &&
                      styles["date--range"],
                  ]
                : value?.standardFormat({ jalali: true }) == formatedDate &&
                    styles["date--selected"],
            )}
            onClick={() => onDateClick?.(d)}
          >
            {i + 1}
          </div>
        );
      }),
    ],
    [month, value, offsetValue],
  );
  return (
    <div className={styles["calendar"]}>
      <div className={styles["calendar__week"]}>
        {week.map((i) => (
          <div key={i + "_week"}>{i}</div>
        ))}
      </div>
      <div className={styles["calendar__dates"]}>{dates}</div>
    </div>
  );
};

export default Calendar;
